﻿import { useState, useEffect, useRef } from "react";

import { products } from "../../../BookingFlowPage/Scripts/Endpoints";
import controller from "../../../../../Resources/Scripts/_Shared/controller";
import { myAimoParkConfirm } from "../../../_Shared/Scripts/MyAimoParkConfirm";
import ExpandableListItem from "./ExpandableListItem";
import { parseDate } from "../../../../../Resources/Scripts/_Shared/parseDate";
import { getFilterMode } from "../utils/filterMode.js";
import lockedIcon from "../../../../../publicResources/icons/locked.svg";
import { LockedPmcPopup } from "../../../../../Resources/Scripts/_Shared/elements/Popup.js";
import LockedPmcForm from "../../../FacilityPage/Scripts/LockedPmcForm";
import { getLtpStpKeys } from "../utils/ltpStpKeys";
import { getRecommendedPrice } from "../utils/recommendedPrice.js";

const ListItem = ({
    facilitySearchContext,
    setFacilitySearchContext,
    facility,
    distance,
    url,
    name,
    location,
    address,
    filteredNotes,
    notesForZones,
    uid,
    date,
}) => {
    const lang = {
        ...facilitySearchContext.localization.headings,
        ...facilitySearchContext.localization.inputs,
        ...facilitySearchContext.localization.products,
        ...facilitySearchContext.localization.filter,
        ...facilitySearchContext.episerver,
        ...facilitySearchContext.localization.messages,
    };
    const listItemClass = "facilitysearchpage__listitem";
    const currectDistance =
        distance > 1 ? distance.toFixed(2) : Math.round(distance * 1000);
    const currectDistanceText = distance > 1 ? "km" : "m";
    const selectedLanguage =
        facilitySearchContext.episerver.selectedlanguage === "no"
            ? "nb"
            : facilitySearchContext.episerver.selectedlanguage === "se"
              ? "sv"
              : facilitySearchContext.episerver.selectedlanguage;
    const currentLocale =
        facilitySearchContext.country === "no"
            ? "nb"
            : facilitySearchContext.country === "se"
              ? "sv"
              : facilitySearchContext.country;
    const country = document.documentElement
        ?.getAttribute("country")
        ?.toLowerCase();
    const listItemRef = useRef(null);
    const [pending, setPending] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [availabilityLoading, setAvailabilityLoading] = useState(false);
    const [totalAvailability, setTotalAvailability] = useState(0);
    const [totalNonAvailability, setNonTotalAvailability] = useState(0);
    const [hasPromotion, setHasPromotion] = useState(null);
    const [hasAvailableSpot, setHasAvailableSpot] = useState(null);
    const [hasLowAvailability, setHasLowAvailability] = useState(null);
    const [isBookableOnline, setIsBookableOnline] = useState(null);
    const [servicesList, setServicesList] = useState("");
    const [lowestPriceProduct, setLowestPriceProduct] = useState(null);
    const [recommendedProduct, setRecommendedProduct] = useState(null);
    const [showLockedModal, setShowLockedModal] = useState(false);
    const isRecommendedProductLockedPmc =
        recommendedProduct?.Visibility === "LOCKED";
    const recommendedPriceDisplay = getRecommendedPrice(recommendedProduct);
    const [hasActualLtpProducts, setHasActualLtpProducts] = useState(false);

    let imageAltTextForLocked =
        selectedLanguage == "sv"
            ? "låst ikon"
            : selectedLanguage == "fi"
              ? "lukittu kuvake"
              : selectedLanguage == "nb"
                ? "låst ikon"
                : "locked icon";
    let modalTitle =
        selectedLanguage == "sv"
            ? "Låst prenumeration!"
            : selectedLanguage == "fi"
              ? "Lukittu tilaus!"
              : selectedLanguage == "nb"
                ? "Låst abonnement!"
                : "Locked subscription!";
    let modalDescription =
        selectedLanguage == "sv"
            ? "Ange din kod för att låsa upp denna prenumeration."
            : selectedLanguage == "fi"
              ? "Syötä koodisi avataksesi tämän tilauksen."
              : selectedLanguage == "nb"
                ? "Skriv inn koden din for å låse opp dette abonnementet."
                : "Enter your code to unlock this subscription.";

    const facilityAddress = address.Number
        ? `${address.Street} ${address.Number}`
        : address.Street;
    const facilityName = name === facilityAddress ? facilityAddress : name;

    if (!facilitySearchContext.filterSelection) return null;
    const STP = facilitySearchContext.filterSelection?.STP ?? false;
    const LTP = facilitySearchContext.filterSelection?.LTP ?? false;
    const hasPrivatePmcsOnly = !facility.HasPublicLtp && facility.HasPrivatePmcs;
    const mode = getFilterMode(STP, LTP);
    if (!mode) return null;

    const { isLtpMode, isStpMode } = mode;
    const { STP_KEY, LTP_KEY } = getLtpStpKeys(country);

    /*
     * Recommended price (for LTP) should be visible JUST in Sweden
     */
    const showRecommendedPrice = currentLocale == "sv";

    const [facilityInfo, setFacilityInfo] = useState({
        Address: facilityAddress,
        Preamble: facility.Preamble,
        LongTermOpeningHours: facility.LongTermOpeningHours,
        GiantLeapLink: facility.GiantLeapLink,
        DrivingInstructions: facility.DrivingInstructions,
        HasLTP: facility.HasLTP,
        HasSTP: facility.HasStp,
        HasPrivatePmcs: facility.HasPrivatePmcs,
        FilteredNotes: filteredNotes,
    });
    let totalAvailabilityCount = 0;

    const expandHandler = (e) => {
        setExpanded(!expanded);
        e.preventDefault();
    };

    const [productSelectionOptions, setProductSelectionOptions] = useState([]);

    const availabilityChangeHandler = (productAvailability) => {
        totalAvailabilityCount++;

        const nonAvailabilityCount = listItemRef.current
            ? listItemRef.current.querySelectorAll(
                  '.book button[aria-busy="false"]:disabled',
              ).length
            : 0;
        setNonTotalAvailability(
            totalNonAvailability > nonAvailabilityCount
                ? totalNonAvailability
                : nonAvailabilityCount,
        );

        if (totalAvailabilityCount >= productSelectionOptions.length)
            setAvailabilityLoading(false);
    };

    // Get facility summary/populate products
    useEffect(() => {
        let isSubscribed = true;

        const getFacilitySummary = async () => {
            const allProducts = await products.getProducts({
                bookingController:
                    facilitySearchContext.episerver.bookingController,
                fId: uid,
                lang: selectedLanguage,
                includeNotBookableOnline: true,
                availabilityFrom: date,
            });

            if (allProducts.status === 200 && isSubscribed) {
                const listOfProducts = allProducts.body.Places.map(
                    (zone) => zone.Item2,
                ).flat();

                const isBookable = listOfProducts.some(
                    (product) => !product.OnlineBookingNotAllowed,
                );

                const promotion = listOfProducts.some(
                    (product) =>
                        Array.isArray(product.Promotions) &&
                        product.Promotions.length > 0,
                );

                let productFilter = Object.keys(
                    facilitySearchContext.filterSelection
                        .ParkingSpaceTypeSelection,
                ).find(
                    (key) =>
                        facilitySearchContext.filterSelection
                            .ParkingSpaceTypeSelection[key],
                );

                const ltpPmcFiltered = facility.ParkingZones.flatMap(
                    (z) => z.Pmcs || [],
                )
                    .filter(
                        (p) =>
                            p.Visibility === "PUBLIC" ||
                            p.Visibility === "LOCKED",
                    )
                    .filter((p) => {
                        const filterSel = facilitySearchContext.filterSelection;
                        let pass = true;
                        if (filterSel.Heated) {
                            pass =
                                pass && (p.Heated ?? facility.Heated ?? false);
                        }
                        if (filterSel.MaxVehicleHeight) {
                            const maxHeight =
                                p.MaxVehicleHeight ??
                                facility.MaxVehicleHeight ??
                                facility.Info?.MaxVehicleHeight ??
                                0;
                            pass = pass && maxHeight > 200;
                        }
                        return pass;
                    });

                listOfProducts.forEach((product) => {
                    const matchingProduct = ltpPmcFiltered.find(
                        (p) => p.PmcId === product.Id,
                    );

                    product.ParkingSpaceType = matchingProduct
                        ? matchingProduct.ParkingSpaceType
                        : null;
                    product.PermitUsageType = matchingProduct
                        ? matchingProduct.PermitUsageType
                        : null;
                });

                const getRecommendedProducts = (products) => {
                    let filteredProducts = productFilter
                        ? products.filter(
                              (product) =>
                                  product.ParkingSpaceType === productFilter,
                          )
                        : [];

                    if (filteredProducts.length === 0)
                        filteredProducts = promotion
                            ? products.filter(
                                  (product) =>
                                      !product.OnlineBookingNotAllowed &&
                                      product.Promotions,
                              )
                            : [];

                    if (filteredProducts.length === 0)
                        filteredProducts = products.filter(
                            (product) =>
                                product.Type === "NON_RESERVED" &&
                                !product.OverviewText.toLowerCase().includes(
                                    "elbil",
                                ) &&
                                product.ParkingSpaceType !== "Elbilsplats",
                        );

                    if (filteredProducts.length === 0)
                        filteredProducts = products;

                    return filteredProducts;
                };

                let recommendedProducts = [];
                if (isLtpMode) {
                    const availableProducts = listOfProducts.filter(
                        (product) =>
                            product.MonthlyPrice && product.MonthlyPrice > 0,
                    );

                    recommendedProducts =
                        getRecommendedProducts(availableProducts);

                    if (recommendedProducts.length === 0) {
                        recommendedProducts =
                            getRecommendedProducts(listOfProducts);
                    }

                    // --- Fallback for LOCKED PMC ---
                    let recommendedProductsWithFallback = recommendedProducts;

                    if (
                        !recommendedProductsWithFallback ||
                        recommendedProductsWithFallback.length === 0
                    ) {
                        recommendedProductsWithFallback = listOfProducts.filter(
                            (p) =>
                                p.Visibility === "PUBLIC" ||
                                p.Visibility === "LOCKED",
                        );
                    }

                    const selectedRecommended =
                        recommendedProductsWithFallback.length > 0
                            ? recommendedProductsWithFallback.reduce(
                                  (prev, current) => {
                                      // If prev is null (the first iteration), set it to current
                                      if (!prev) {
                                          return current;
                                      }

                                      // Check if both products have Availability > 0
                                      if (
                                          prev.Availability &&
                                          prev.Availability > 0 &&
                                          current.Availability &&
                                          current.Availability > 0
                                      ) {
                                          // If both have Availability > 0, compare MonthlyPrice
                                          return current.MonthlyPrice &&
                                              current.MonthlyPrice >
                                                  prev.MonthlyPrice
                                              ? current
                                              : prev;
                                      } else if (
                                          prev.Availability &&
                                          prev.Availability > 0
                                      ) {
                                          // If only prev has Availability > 0, select prev
                                          return prev.MonthlyPrice > 0
                                              ? prev
                                              : current;
                                      } else if (
                                          current.Availability &&
                                          current.Availability > 0
                                      ) {
                                          // If only current has Availability > 0, select current
                                          return current.MonthlyPrice > 0
                                              ? current
                                              : prev;
                                      } else {
                                          // If neither has Availability > 0, return the one with a MonthlyPrice > 0
                                          return prev.MonthlyPrice >
                                              current.MonthlyPrice
                                              ? prev
                                              : current;
                                      }
                                  },
                                  null,
                              )
                            : null;

                    setHasActualLtpProducts(
                        recommendedProductsWithFallback.length > 0,
                    );
                    setRecommendedProduct(selectedRecommended);
                } else {
                    setHasActualLtpProducts(false);
                }

                setIsBookableOnline(isBookable);
                setHasPromotion(promotion);
                setProductSelectionOptions(allProducts.body);
                setFacilityInfo({
                    ...allProducts.body,
                    ...facilityInfo,
                });
                if (
                    isBookable &&
                    !facilitySearchContext.episerver.hideAvailability
                ) {
                    setHasAvailableSpot(
                        lang.availability &&
                            listOfProducts.some(
                                (product) =>
                                    !product.OnlineBookingNotAllowed &&
                                    product.Availability &&
                                    product.Availability > 0,
                            ),
                    );
                    setHasLowAvailability(
                        lang.availability &&
                            !listOfProducts.some(
                                (product) =>
                                    !product.OnlineBookingNotAllowed &&
                                    product.Availability &&
                                    product.Availability > 4,
                            ),
                    );
                }
            } else if (isSubscribed) {
                setFacilityInfo({
                    ...facilityInfo,
                    HasLTPBookableOnline: false,
                    HasLTPNotBookableOnline: false,
                });
                setLowestPriceProduct(false);
                setRecommendedProduct(false);
                setHasActualLtpProducts(false);
                setHasAvailableSpot(false);
                setHasLowAvailability(false);
                setHasPromotion(false);
            }
            if (isSubscribed) setPending(false);
        };
        setPending(true);
        getFacilitySummary();
        return () => (isSubscribed = false);
    }, [
        date,
        facility?.ParkingZones,
        facilitySearchContext.filterSelection.LTP,
        facilitySearchContext.filterSelection.STP,
        facilitySearchContext.filterSelection.Heated,
        facilitySearchContext.filterSelection.MaxVehicleHeight,
        facilitySearchContext.filterSelection.ParkingSpaceTypeSelection,
    ]);

    // Toggle LTP/STP visual and controller
    useEffect(() => {
        if (
            facilityInfo.HasWaitingList &&
            facilityInfo.FilteredNotes &&
            !pending
        ) {
            controller.add("myaimopark-confirm", myAimoParkConfirm);
            controller.parse(listItemRef.current);
        }
        const facilitySearchPageWrapper = document.querySelector(
            ".facilitysearchpage__wrapper",
        );
        if (!pending) {
            facilitySearchPageWrapper.classList.toggle("STP", STP);
            facilitySearchPageWrapper.classList.toggle("LTP", LTP);
        }
    }, [facilityInfo, pending]);

    // Set visual services
    useEffect(() => {
        if (!facility) return;

        const services = [];
        if (facility.Indoor)
            services.push(
                lang.info.indoor.toLowerCase().replaceAll("&shy;", "\xAD"),
            );
        if (!facility.Indoor)
            services.push(
                lang.info.outdoor.toLowerCase().replaceAll("&shy;", "\xAD"),
            );
        if (facility.Info && facility.Info.Heated)
            services.push(
                lang.info.heated.toLowerCase().replaceAll("&shy;", "\xAD"),
            );
        if (
            facility.Info &&
            facility.Info.MaxVehicleHeight &&
            facility.Info.MaxVehicleHeight > 200
        )
            services.push(
                lang.info.maxVehicleHeight
                    .toLowerCase()
                    .replaceAll("&shy;", "\xAD"),
            );
        if (
            facility.Services &&
            facility.Services.includes("electricChargingPoint") &&
            selectedLanguage === "en"
        )
            services.push(
                lang.services.electricchargingpoint.replaceAll("&shy;", "\xAD"),
            );
        if (
            facility.Services &&
            facility.Services.includes("electricChargingPoint") &&
            selectedLanguage !== "en"
        )
            services.push(
                lang.services.electricchargingpoint
                    .toLowerCase()
                    .replaceAll("&shy;", "\xAD"),
            );
        if (facility.Services && facility.Services.includes("disabled"))
            services.push(
                lang.services.disabled
                    .toLowerCase()
                    .replaceAll("&shy;", "\xAD"),
            );
        const hasPrivatePmcsOnly = !facility.HasPublicLtp && facility.HasPrivatePmcs;
        if (facility.HasStp) services.push(lang.info[STP_KEY]?.toLowerCase());
        if (facility.HasLtp || hasPrivatePmcsOnly) services.push(lang.info[LTP_KEY]?.toLowerCase());
        if (services.length) {
            services[services.length - 1] += ".";
            services[0] =
                services[0].charAt(0).toUpperCase() + services[0].slice(1);
        }
        setServicesList(services.join(", "));
    }, []);

    // Reset products and recommended product
    useEffect(() => {
        setProductSelectionOptions([]);
        setRecommendedProduct(null);
    }, [facility?.ParkingZones]);

    const showOnMap = (e) => {
        e.preventDefault();
        const facilityUid =
            listItemRef.current.getAttribute("data-facility-uid");
        const selectedFacility = facilitySearchContext.allFacilities.find(
            (f) => f.Uid === facilityUid,
        );
        setFacilitySearchContext({
            ...facilitySearchContext,
            showOnMap: selectedFacility,
        });
    };

    const showRecommendedPriceArea = showRecommendedPrice && isLtpMode;

    const isRecommendedProductSoldOut =
        !!recommendedProduct &&
        recommendedProduct.Availability == 0 &&
        !recommendedProduct.AvailableFrom;

    // The facility service tag only says the facility offers queues at all, the queue is per PMC
    const canQueueForRecommendedProduct =
        isRecommendedProductSoldOut &&
        recommendedProduct.EnableWaitingList === true &&
        !!facilityInfo.HasWaitingList &&
        !!facilityInfo.WaitingListUrl;

    return (
        <li className={listItemClass} ref={listItemRef} data-facility-uid={uid}>
            <section className="left">
                <div className="top">
                    {showRecommendedPriceArea && hasAvailableSpot ? (
                        <div
                            className="left--availablespot"
                            data-low={hasLowAvailability}
                        >
                            <div>
                                <a
                                    href={url}
                                    onClick={(e) => {
                                        if (!url) e.preventDefault();
                                    }}
                                >
                                    {facilityName}
                                </a>
                            </div>
                        </div>
                    ) : (
                        <a
                            href={url}
                            onClick={(e) => {
                                if (!url) e.preventDefault();
                            }}
                        >
                            {facilityName}
                        </a>
                    )}
                    {/*showRecommendedPriceArea && hasPromotion && (
                        <img
                            src={`/publicResources/icons/promo/${selectedLanguage}/Promotion2_${selectedLanguage}.svg`}
                            alt="promotion icon"
                        />
                    )*/}
                </div>
                <div className="distance">
                    {currectDistance} {currectDistanceText} {lang.from}{" "}
                    {location}
                </div>
                <div className="showonmap">
                    {facilityAddress}
                    <button onClick={showOnMap}>{lang.showonmap}</button>
                </div>
                {servicesList.length > 0 && (
                    <div className="services">{servicesList}</div>
                )}
            </section>

            <section
                className={`right ${isStpMode ? "hidden" : ""}`}
                data-controller={
                    facilityInfo.HasWaitingListUrl !== "null"
                        ? "myaimopark-confirm"
                        : ""
                }
                data-confirm-label-text={lang.confirmlabel}
                data-confirm-close-text={lang.close}
                data-confirm-cancel-text={lang.cancel}
                data-confirm-confirm-text={lang.continue}
                data-confirm-heading-text={lang.notes}
            >
                {showRecommendedPriceArea && (
                    <>
                        {!pending &&
                            recommendedProduct &&
                            recommendedProduct.Promotions && (
                                <img
                                    className="facilitysearchpage__listitem--promo"
                                    src={`/publicResources/icons/promo/${selectedLanguage}/Promotion1_${selectedLanguage}.svg`}
                                    alt="promotion icon"
                                />
                            )}
                        {hasActualLtpProducts && recommendedProduct && (
                            <div
                                className={`recommendedprice ${pending ? "loading" : ""}`}
                            >
                                {pending ? (
                                    <div className="aimo-spinner">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            {lang.priceex}
                                            {!isRecommendedProductLockedPmc &&
                                                recommendedProduct && (
                                                    <span className="monthlyPrice">
                                                        {recommendedPriceDisplay?.amount ??
                                                            0}{" "}
                                                        {
                                                            recommendedProduct.Currency
                                                        }
                                                        /
                                                        {
                                                            lang[
                                                                recommendedPriceDisplay?.periodKey ??
                                                                    "month"
                                                            ]
                                                        }
                                                    </span>
                                                )}
                                            <span className="pmcName">
                                                {recommendedProduct &&
                                                    recommendedProduct.OverviewText}
                                                {isRecommendedProductLockedPmc && (
                                                    <img
                                                        src={lockedIcon}
                                                        alt={
                                                            imageAltTextForLocked
                                                        }
                                                    />
                                                )}
                                            </span>
                                            {recommendedProduct &&
                                                !recommendedProduct.Availability &&
                                                recommendedProduct.AvailableFrom && (
                                                    <span className="start-date">
                                                        {lang.availablefrom}{" "}
                                                        {parseDate(
                                                            recommendedProduct.AvailableFrom,
                                                            currentLocale,
                                                            "L",
                                                        )}
                                                    </span>
                                                )}
                                        </div>
                                        {canQueueForRecommendedProduct ? (
                                            <a
                                                href={url}
                                                onClick={(e) => {
                                                    if (!url)
                                                        e.preventDefault();
                                                }}
                                                className="facilitysearchpage__listitem__cta facilitysearchpage__listitem__cta--list"
                                                data-ltp-booking="interestlist"
                                                data-confirm="facility"
                                                data-confirm-message-text={
                                                    facilityInfo.FilteredNotes
                                                }
                                            >
                                                {lang.waitinglist}
                                            </a>
                                        ) : isRecommendedProductSoldOut ? (
                                            <a
                                                href={url}
                                                onClick={(e) => {
                                                    if (!url)
                                                        e.preventDefault();
                                                }}
                                                className="facilitysearchpage__listitem__cta facilitysearchpage__listitem__cta--list facilitysearchpage__listitem__cta--red"
                                                data-ltp-booking="interestlist"
                                                data-confirm="facility"
                                                data-confirm-message-text={
                                                    facilityInfo.FilteredNotes
                                                }
                                            >
                                                {lang.full}
                                            </a>
                                        ) : isRecommendedProductLockedPmc ? (
                                            <>
                                                <button
                                                    className="facilitysearchpage__listitem__cta"
                                                    onClick={() =>
                                                        setShowLockedModal(true)
                                                    }
                                                >
                                                    {lang.book}
                                                </button>
                                            </>
                                        ) : recommendedProduct &&
                                          recommendedProduct.OnlineBookingNotAllowed ? (
                                            <a
                                                href={url}
                                                onClick={(e) => {
                                                    if (!url)
                                                        e.preventDefault();
                                                }}
                                                className="facilitysearchpage__listitem__cta facilitysearchpage__listitem__cta--list facilitysearchpage__listitem__cta--shorter"
                                                data-ltp-booking="interestlist"
                                                data-confirm="facility"
                                                data-confirm-message-text={
                                                    facilityInfo.FilteredNotes
                                                }
                                            >
                                                Anm&#228;l intresse
                                            </a>
                                        ) : (
                                            <a
                                                href={
                                                    lang.bookingflowUrl +
                                                    "?fId=" +
                                                    uid +
                                                    "&t=1"
                                                }
                                                className="facilitysearchpage__listitem__cta"
                                                data-ltp-booking="ltponline"
                                            >
                                                {lang.book}
                                            </a>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}
            </section>

            <button
                className="showmore"
                disabled={
                    !expanded &&
                    (isLtpMode && hasActualLtpProducts
                        ? recommendedProduct === null ||
                          Object.keys(facilityInfo).length === 0 ||
                          pending
                        : Object.keys(facilityInfo).length === 0 || pending)
                }
                onClick={expandHandler}
                aria-expanded={expanded}
            >
                <span>
                    {expanded
                        ? lang.showless
                        : isLtpMode && hasActualLtpProducts
                          ? lang.showmoreinfoforltp
                          : lang.showmoreinfo}
                </span>
            </button>

            <ExpandableListItem
                STPMode={isStpMode}
                campaignsPending={pending}
                facilitySearchContext={facilitySearchContext}
                uid={uid}
                date={date}
                expanded={expanded}
                products={productSelectionOptions}
                facilityInfo={facilityInfo}
                filteredNotes={filteredNotes}
                notesForZones={notesForZones}
                availabilityChangeHandler={availabilityChangeHandler}
                hasPrivatePmcsOnly={hasPrivatePmcsOnly}
            />

            <LockedPmcPopup
                title={modalTitle}
                description={modalDescription}
                visible={showLockedModal}
                onClose={() => setShowLockedModal(false)}
            >
                <LockedPmcForm
                    lang={selectedLanguage}
                    pmcId={recommendedProduct?.Id}
                    onSuccess={() => {
                        setShowLockedModal(false);
                    }}
                    onSubmit={() => {
                        setShowLockedModal(false);

                        const bookingUrl =
                            lang.bookingflowUrl +
                            "?fId=" +
                            uid +
                            "&t=1&pid=" +
                            recommendedProduct.ZoneAndProduct +
                            "&verifiedLockedCode=checkedAndValid";

                        window.location.href = bookingUrl;
                    }}
                />
            </LockedPmcPopup>
        </li>
    );
};

export default ListItem;
